import styled from "styled-components";

interface Props {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
}

const getPageRange = (current: number, total: number): (number | "…")[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | "…")[] = [1];
  if (current > 3) pages.push("…");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let p = start; p <= end; p++) pages.push(p);
  if (current < total - 2) pages.push("…");
  pages.push(total);
  return pages;
};

export const Pagination = ({ currentPage, totalPages, onChange }: Props) => {
  if (totalPages <= 1) return null;
  const pages = getPageRange(currentPage, totalPages);

  return (
    <Wrapper>
      <NavBtn
        type="button"
        aria-label="Назад"
        disabled={currentPage === 1}
        onClick={() => onChange(currentPage - 1)}
      >
        <svg viewBox="0 0 10 14" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M8 1L2 7L8 13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </NavBtn>
      {pages.map((p, i) =>
        p === "…" ? (
          <Ellipsis key={`e${i}`}>…</Ellipsis>
        ) : (
          <PageBtn
            key={p}
            type="button"
            $active={p === currentPage}
            onClick={() => onChange(p as number)}
          >
            {p}
          </PageBtn>
        )
      )}
      <NavBtn
        type="button"
        aria-label="Вперёд"
        disabled={currentPage === totalPages}
        onClick={() => onChange(currentPage + 1)}
      >
        <svg viewBox="0 0 10 14" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2 1L8 7L2 13"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </NavBtn>
    </Wrapper>
  );
};

const Wrapper = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 40px auto 0;
  flex-wrap: wrap;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: var(--radius-md);
  background: white;
  border: 1.5px solid var(--color-border);
  color: var(--color-primary);
  cursor: pointer;
  transition: var(--transition);

  svg {
    width: 9px;
    height: 12px;
  }

  &:hover:not(:disabled) {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: white;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageBtn = styled.button<{ $active: boolean }>`
  min-width: 38px;
  height: 38px;
  padding: 0 12px;
  border-radius: var(--radius-md);
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: var(--transition);
  background: ${({ $active }) =>
    $active ? "var(--color-primary)" : "white"};
  color: ${({ $active }) =>
    $active ? "white" : "var(--color-text)"};
  border: 1.5px solid
    ${({ $active }) =>
      $active ? "var(--color-primary)" : "var(--color-border)"};
  box-shadow: ${({ $active }) =>
    $active ? "0 6px 14px rgba(91, 139, 15, 0.25)" : "none"};

  &:hover {
    border-color: var(--color-primary);
    color: ${({ $active }) =>
      $active ? "white" : "var(--color-primary)"};
    background: ${({ $active }) =>
      $active ? "var(--color-primary)" : "var(--color-primary-soft)"};
  }
`;

const Ellipsis = styled.span`
  min-width: 28px;
  text-align: center;
  color: var(--color-text-muted);
  font-weight: 700;
  user-select: none;
`;
